import { useApolloClient } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useFlags } from "flagsmith/react";
import Markdown from "markdown-to-jsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RiArrowDownSLine, RiDownloadLine, RiUploadLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { useBoundariesMapByEnrollmentPeriod } from "src/components/Boundary/useBoundary";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { MenuItemWithDescription } from "src/components/Menus/MenuItemWithDescription";
import { Glossary } from "src/components/Text/Glossary";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { evictEnrollmentSchoolsGradesCache } from "src/services/apolloCacheManager";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { useEnrollmentPeriodContext } from "../Edit";
import { ExportBoundaryConnectionMenuItem } from "./components/ExportBoundaryConnectionMenuItem";
import { ExportButtonLegacy } from "./components/ExportButtonLegacy";
import { ExportSchoolMenuItem } from "./components/ExportSchoolMenuItem";
import { ImportButtonLegacy } from "./components/ImportButtonLegacy";
import { ImportConnectionsFromJSON } from "./components/ImportConnectionsFromJSON";
import { ImportEnrollmentSchoolDialog } from "./components/ImportEnrollmentSchoolDialog";
import { ImportSchoolsFromJSON } from "./components/ImportSchoolsFromJSON";
import { SchoolGradeProgramsCard } from "./components/SchoolGradeProgramsCard";
import { SchoolGradesCard } from "./components/SchoolGradesCard";
import { GET_ENROLLMENT_SCHOOLS_AND_CONFIGS } from "./graphql/queries";
import {
  BoundaryConnectionError,
  ImportBoundaryConnectionsResult,
  ImportResult,
  onImportCompleteCallback,
} from "./types";

export const EnrollmentSchoolsGradesView: React.FC = () => {
  const organization = useOrganization();
  const { glossary } = useGlossary();
  const { enrollmentPeriod, actionBarRef, setShowFormButtons } =
    useEnrollmentPeriodContext();
  const toast = useAvelaToast();
  const flags = useFlags(["enrollment-schools-export-import"]);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get("school_filter") || ""
  );
  const [showImportSchoolsJSON, setShowImportSchoolsJSON] = useState(false);
  const [showImportConnectionsJSON, setShowImportConnectionsJSON] =
    useState(false);
  const apolloClient = useApolloClient();

  useEffect(() => {
    setShowFormButtons(showImportSchoolsJSON || showImportConnectionsJSON);
    return () => {
      setShowFormButtons(false);
    };
  }, [setShowFormButtons, showImportSchoolsJSON, showImportConnectionsJSON]);

  const { remoteData: enrollmentSchoolsRemoteData, refetch } =
    useRemoteDataQuery<
      GQL.GetEnrollmentSchoolsAndConfigs,
      GQL.GetEnrollmentSchoolsAndConfigsVariables
    >(GET_ENROLLMENT_SCHOOLS_AND_CONFIGS, {
      variables: {
        organization_id: organization.map((org) => org.id).toNullable(),
        order_by: { name: GQL.order_by.asc },
      },
      skip: !organization.hasData(),
      notifyOnNetworkStatusChange: true,
    });

  const [importErrors, setImportErrors] = useState<
    FSR.ImportSchoolsAndGradesForEnrollmentPeriodResponse["data"]["errors"]
  >([]);

  const [importBoundaryConnectionErrors, setImportBoundaryConnectionErrors] =
    useState<FSR.ImportSchoolBoundaryConnectionsResponse["data"]["errors"]>([]);

  const {
    isOpen: isImportFormDialogOpen,
    onOpen: onImportFormDialogOpen,
    onClose: onImportFormDialogClose,
  } = useDisclosure();

  const { remoteData: boundaryMapRemoteData, refetch: refetchBoundaryMap } =
    useBoundariesMapByEnrollmentPeriod({
      enrollmentPeriodId: enrollmentPeriod.id,
    });

  const remoteData = useMemo(() => {
    return RD.toTuple(enrollmentSchoolsRemoteData, boundaryMapRemoteData);
  }, [boundaryMapRemoteData, enrollmentSchoolsRemoteData]);

  const handleBulkUploadComplete = () => {
    refetch();
    toast({
      id: "upload-grades-success",
      title: "Grades uploaded",
      isClosable: true,
      status: "success",
    });
  };

  const onImportSchoolsComplete: onImportCompleteCallback = useCallback(
    async (result: ImportResult) => {
      setImportErrors(result.errors || []);
      setImportBoundaryConnectionErrors(result.connectionErrors || []);
      setShowImportSchoolsJSON(false);

      try {
        await apolloClient.refetchQueries({
          updateCache: evictEnrollmentSchoolsGradesCache,
        });
        await refetchBoundaryMap();
      } catch (error) {
        console.error("onImportComplete error", error);
        toast.genericError();
      }
    },
    [apolloClient, toast, refetchBoundaryMap]
  );

  const onImportConnectionsComplete = useCallback(
    async (result: ImportBoundaryConnectionsResult) => {
      setImportBoundaryConnectionErrors(result.errors || []);
      setShowImportConnectionsJSON(false);

      try {
        await apolloClient.refetchQueries({
          updateCache: evictEnrollmentSchoolsGradesCache,
        });
        await refetch();
        await refetchBoundaryMap();
      } catch (error) {
        console.error("onImportComplete error", error);
        toast.genericError();
      }
    },
    [refetch, refetchBoundaryMap, apolloClient, toast]
  );

  const dismissIndividualError = useCallback(
    (error: { id?: string; level: string; message: string }) => {
      setImportErrors((prevErrors) =>
        prevErrors.filter(
          (prevError) =>
            !(
              error.id === prevError.id &&
              error.level === prevError.level &&
              error.message === prevError.message
            )
        )
      );
    },
    []
  );

  const dismissBoundaryConnectionError = useCallback(
    (data: BoundaryConnectionError) => {
      setImportBoundaryConnectionErrors((prevErrors) =>
        prevErrors.filter((prevError) => !(data.id === prevError.id))
      );
    },
    []
  );

  if (!organization.hasData()) {
    return null;
  }

  // Show import JSON component when state is true
  if (showImportSchoolsJSON) {
    return (
      <ImportSchoolsFromJSON
        onBack={() => setShowImportSchoolsJSON(false)}
        onComplete={onImportSchoolsComplete}
        enrollmentPeriodId={enrollmentPeriod.id}
        actionBarRef={actionBarRef}
      />
    );
  } else if (showImportConnectionsJSON) {
    return (
      <ImportConnectionsFromJSON
        onBack={() => setShowImportConnectionsJSON(false)}
        onComplete={onImportConnectionsComplete}
        enrollmentPeriodId={enrollmentPeriod.id}
        actionBarRef={actionBarRef}
      />
    );
  }

  return (
    <VStack align="left">
      <ImportEnrollmentSchoolDialog
        onClose={onImportFormDialogClose}
        isOpen={isImportFormDialogOpen}
        targetEnrollmentPeriodId={enrollmentPeriod.id}
        onImportComplete={onImportSchoolsComplete}
      />

      <HStack justifyContent="end">
        <Flex justifyContent="right" gap={4}>
          {flags["enrollment-schools-export-import"].enabled ? (
            <>
              <Menu>
                <MenuButton
                  as={Button}
                  leftIcon={<RiUploadLine />}
                  rightIcon={<RiArrowDownSLine />}
                  variant="outline"
                >
                  Import
                </MenuButton>
                <MenuList>
                  <MenuItemWithDescription
                    title="Schools from JSON file"
                    description="Replace this period's schools from a file"
                    onClick={() => setShowImportSchoolsJSON(true)}
                  />
                  <MenuItemWithDescription
                    title="Boundaries from JSON file"
                    description="Add or replace boundary connections from a file"
                    onClick={() => setShowImportConnectionsJSON(true)}
                  />
                  <MenuItemWithDescription
                    onClick={onImportFormDialogOpen}
                    title="From existing form"
                    description="Replace content from an existing enrollment period"
                  />
                </MenuList>
              </Menu>
              <Menu>
                <MenuButton
                  as={Button}
                  leftIcon={<RiDownloadLine />}
                  rightIcon={<RiArrowDownSLine />}
                  variant="solid"
                >
                  Export
                </MenuButton>
                <MenuList>
                  <ExportSchoolMenuItem
                    enrollmentPeriodId={enrollmentPeriod.id}
                  />
                  <ExportBoundaryConnectionMenuItem
                    enrollmentPeriodId={enrollmentPeriod.id}
                  />
                </MenuList>
              </Menu>
            </>
          ) : (
            <>
              <Box>
                <ImportButtonLegacy onComplete={handleBulkUploadComplete} />
              </Box>
              <Box>
                <ExportButtonLegacy
                  organizationId={organization.data.id}
                  enrollmentPeriodId={enrollmentPeriod.id}
                />
              </Box>
            </>
          )}
        </Flex>
      </HStack>

      {importErrors.length > 0 && (
        <Flex direction="column" gap="2">
          {importErrors.map(({ id, level, message }) => (
            <Alert
              key={`import-error-${level}-${id}`}
              status="error"
              rounded="md"
              position="relative"
            >
              <AlertIcon />
              <AlertDescription>
                <Markdown>{message}</Markdown>
              </AlertDescription>
              <CloseButton
                alignSelf="flex-end"
                position="absolute"
                top="0.5rem"
                right="0.5rem"
                onClick={() => dismissIndividualError({ id, level, message })}
              />
            </Alert>
          ))}
        </Flex>
      )}

      {importBoundaryConnectionErrors.length > 0 && (
        <Flex direction="column" gap="2">
          {importBoundaryConnectionErrors.map(({ id, message }) => (
            <Alert
              key={`import-boundary-connection-error-${id}`}
              status="error"
              rounded="md"
              position="relative"
            >
              <AlertIcon />
              <AlertDescription>{message}</AlertDescription>
              <CloseButton
                alignSelf="flex-end"
                position="absolute"
                top="0.5rem"
                right="0.5rem"
                onClick={() => dismissBoundaryConnectionError({ id, message })}
              />
            </Alert>
          ))}
        </Flex>
      )}

      <GQLRemoteDataView remoteData={remoteData}>
        {([
          {
            school: schools,
            grade_config: gradeConfigs,
            program_group: programGroups,
          },
          boundaryMap,
        ]) => {
          if (schools.length === 0) {
            return (
              <Text color="blackAlpha.700">
                <Glossary>You have not uploaded any schools</Glossary>
              </Text>
            );
          }
          const sortedSchools = [...schools].sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
          const filteredSchools = searchQuery.trim()
            ? sortedSchools.filter((school) =>
                school.name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase().trim())
              )
            : sortedSchools;
          return (
            <VStack gap={6}>
              <SearchInput
                placeholder={glossary`Search schools by name...`}
                value={searchQuery}
                onSearchChange={setSearchQuery}
                onClear={() => setSearchQuery("")}
                variant="leftIcon"
              />
              {filteredSchools.map((school) => {
                // For now, assume there is 0 or 1 program group per org.
                const programs = programGroups[0]?.programs;

                const schoolGrades = school.grades.filter(
                  (grade) => grade.enrollment_period_id === enrollmentPeriod.id
                );

                return programs && programs.length > 0 ? (
                  <SchoolGradeProgramsCard
                    organizationId={organization.data.id}
                    schoolId={school.id}
                    schoolName={school.name}
                    enrollmentPeriodId={enrollmentPeriod.id}
                    gradeConfigs={gradeConfigs}
                    grades={schoolGrades}
                    programConfigs={programs}
                    key={school.id}
                    boundaryMap={boundaryMap}
                    refetchBoundaryMap={refetchBoundaryMap}
                  />
                ) : (
                  <SchoolGradesCard
                    organizationId={organization.data.id}
                    schoolId={school.id}
                    schoolName={school.name}
                    enrollmentPeriodId={enrollmentPeriod.id}
                    gradeConfigs={gradeConfigs}
                    grades={schoolGrades}
                    key={school.id}
                    boundaryMap={boundaryMap}
                    refetchBoundaryMap={refetchBoundaryMap}
                  />
                );
              })}
            </VStack>
          );
        }}
      </GQLRemoteDataView>
    </VStack>
  );
};
